import { Chart as ChartJs } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chroma from 'chroma-js';
import IconButton from 'components/IconButton';
import history from 'history.js';
import { showWarningToast } from 'lib/ToastHelper';
import React, { useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle, Col, Row } from 'reactstrap';

import { getHistoryChartData } from '../../../components/HistoryChart/helper';
import {
  LINE_CHART_OPTIONS,
  LINE_CHART_OPTIONS_NO_COUNT_LABELS,
} from '../../../components/HistoryChart/options';
import { IntervalFilter, TooltipLabelsType } from '../../../types';

import 'views/Admin/Dashboard/styles.css';

interface Props {
  title: string;
  subtitle?: string;
  labels: string[];
  tooltipLabels: TooltipLabelsType;
  datasets: number[][];
  colors: string[];
  showCounts: boolean;
  isActive?: boolean;
  lastSyncedDate?: string;
  liveActiveCount?: number;
  newDataGenerationIndex: number;
  lastIntervalDrillDownUrl?: string;
  getDrillDownUrl: (sourceType: string) => string;
  onExpandClick?: () => void;
  lastIntervalCount?: number;
  selectedInterval?: IntervalFilter;
  drillDownRequiresWeeklyInterval?: boolean;
}

const TrendingHistoryChart = ({
  title,
  subtitle,
  labels,
  tooltipLabels,
  datasets,
  colors,
  showCounts,
  isActive = false,
  lastSyncedDate,
  liveActiveCount,
  newDataGenerationIndex,
  lastIntervalDrillDownUrl,
  getDrillDownUrl,
  onExpandClick,
  lastIntervalCount,
  selectedInterval,
  drillDownRequiresWeeklyInterval = false,
}: Props) => {
  const chartReference = useRef<ChartJs<'line'> | null>(null);
  const themeMode = localStorage.getItem('mode');

  useEffect(() => {
    const chart = chartReference.current;
    if (chart) {
      chart.options = showCounts
        ? LINE_CHART_OPTIONS
        : LINE_CHART_OPTIONS_NO_COUNT_LABELS;
      chart.update();
    }
  }, [showCounts, themeMode]);

  const totalCount = datasets.reduce(
    (total, dataset) => total + (dataset[dataset.length - 1] || 0),
    0,
  );

  const isDrillDownAvailable =
    !drillDownRequiresWeeklyInterval ||
    selectedInterval === IntervalFilter.Week;

  const showDrillDownToast = () => {
    showWarningToast('Drilldown only available when Interval is 1w.');
  };

  const navigateTo = (url: string) => {
    if (!isDrillDownAvailable) {
      showDrillDownToast();
      return;
    }
    if (url) history.push(url);
  };

  const handleChartClick = (event, chart: ChartJs<'line'> | null) => {
    if (!chart) return;
    if (!isDrillDownAvailable) {
      showDrillDownToast();
      return;
    }
    const activeElements = chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      true,
    );

    if (activeElements.length > 0) {
      const activeElement = activeElements[0];
      const dataset = chart.data.datasets[activeElement.datasetIndex];
      const url = getDrillDownUrl(dataset.label || '');
      navigateTo(url);
      return;
    }

    navigateTo(getDrillDownUrl(''));
  };

  const renderLegends = () => (
    <div className="mr-1" style={{ cursor: 'pointer' }}>
      {tooltipLabels.map((tooltip, index) => {
        const color = colors[index];
        return (
          <div
            key={`${tooltip.label}-${index}`}
            className="d-inline"
            onClick={() => navigateTo(getDrillDownUrl(tooltip.label))}
          >
            <div
              className="m-1 p-1 legend-box"
              style={{
                borderColor: color,
                backgroundColor: chroma(color).alpha(0.3).css(),
              }}
            />
            <span className="d-inline legend-text">
              {tooltip.hostsCount !== null
                ? `${tooltip.label} (${tooltip.hostsCount})`
                : tooltip.label}
            </span>
          </div>
        );
      })}
    </div>
  );

  const headlineUrl = getDrillDownUrl('All');
  const headline = (
    <div className="d-flex flex-row align-items-baseline">
      <h1 className="d-inline mb-0">{totalCount}</h1>
      <h5 className="card-category px-1">{title}</h5>
      <h5 className="card-category px-1" style={{ textTransform: 'none' }}>
        {totalCount !== liveActiveCount &&
        liveActiveCount !== undefined &&
        lastSyncedDate
          ? `(Last Synchronized at: ${lastSyncedDate})`
          : ''}
      </h5>
    </div>
  );

  return (
    <Card className="card-chart">
      <CardHeader>
        <Row className="justify-content-between">
          <Col className="col-auto pr-0">
            <CardTitle tag="div" className="mb-0">
              {isDrillDownAvailable ? (
                <Link to={headlineUrl}>{headline}</Link>
              ) : (
                headline
              )}
            </CardTitle>
          </Col>
          <Col className="col p-0">
            <div className="col-12 d-flex flex-row-reverse">
              {onExpandClick && (
                <IconButton
                  id={`bpa-expand-${title.toLowerCase()}`}
                  iconClass={
                    isActive
                      ? 'bi bi-arrows-angle-contract'
                      : 'bi bi-arrows-angle-expand'
                  }
                  tooltip={isActive ? 'Contract' : 'Expand'}
                  size="sm"
                  cssClass="padding-2"
                  active={isActive}
                  onClick={onExpandClick}
                />
              )}
            </div>
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col className="col-auto pr-0">
            <CardTitle tag="div" className="mb-0">
              {subtitle && lastIntervalDrillDownUrl && (
                <div
                  className="d-flex flex-row align-items-baseline"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigateTo(lastIntervalDrillDownUrl)}
                >
                  <h4 className="px-1 font-weight-600">{lastIntervalCount}</h4>
                  <p>{subtitle}</p>
                </div>
              )}
            </CardTitle>
          </Col>
          <Col className="col p-0">
            <div className="col-12 d-flex flex-row-reverse">
              {renderLegends()}
            </div>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        <div
          className="chart-area"
          style={{ cursor: 'pointer', height: '220px', width: '100%' }}
        >
          <Line
            ref={(node) => {
              if (node != null) chartReference.current = node;
            }}
            options={
              showCounts
                ? LINE_CHART_OPTIONS
                : LINE_CHART_OPTIONS_NO_COUNT_LABELS
            }
            data={getHistoryChartData(
              labels,
              tooltipLabels,
              datasets,
              colors,
              newDataGenerationIndex,
            )}
            plugins={[ChartDataLabels]}
            onClick={(event) => handleChartClick(event, chartReference.current)}
          />
        </div>
      </CardBody>
    </Card>
  );
};

export default TrendingHistoryChart;
